<script>
import { log } from "@/utils/clientLogger.js";
import helpers from "@root/helpers.js";
import { useI18n } from 'vue-i18n';
import $http from "@/axios.config";

export default {
  name: "OrderAudioNotes",
  props: {
    orderId: {
      type: String,
      default: null
    },
    audioRecords: {
      type: Array,
      default: () => []
    },
    isOrderCreator: {
      type: Boolean,
      default: false
    }
  },
  emits: ['uploaded', 'delete', 'read', 'update:isSubmitting'],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      isCollapseOpen: true,
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      mediaStream: null,
    };
  },
  mounted() {
    if (this.orderId) {
      const savedState = localStorage.getItem(`audioCollapseState_${this.orderId}`);
      if (savedState !== null) {
        this.isCollapseOpen = JSON.parse(savedState);
      }
    }
  },
  beforeUnmount() {
    if (this.isRecording || this.mediaStream) {
      log.warn('View:OrderAudioNotes:beforeUnmount', { 'message': 'Component destroyed, forced recording stop.' });

      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
      }

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
      }

      this.isRecording = false;
      this.audioChunks = [];
      this.mediaRecorder = null;
      this.mediaStream = null;
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapseOpen = !this.isCollapseOpen;
      if (this.orderId) {
        localStorage.setItem(`audioCollapseState_${this.orderId}`, JSON.stringify(this.isCollapseOpen));
      }
    },
    formatFullDate(date) {
      return helpers.formatFullDate(date);
    },
    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.mediaStream = stream;
        this.mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          log.info('View:OrderAudioNotes:ondataavailable', {
            'message': 'Audio chunk received',
            'size': event.data.size
          });
          this.audioChunks.push(event.data);
        };

        this.mediaRecorder.onstop = this.uploadFormRecording;
        this.mediaRecorder.start();
        this.isRecording = true;

      } catch (err) {
        log.error('View:OrderAudioNotes:startRecording', { 'error': 'No access to microphone', 'details': err });
        this.$swal.fire(this.t('alerts.error'), this.t('messages.noMicrophoneAccess'), 'error');
      }
    },
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        log.info('View:OrderAudioNotes:stopRecording', {
          'message': 'Stopping recorder.',
          'state': this.mediaRecorder.state
        });
        this.mediaRecorder.stop();
        this.isRecording = false;
      }
    },
    async uploadFormRecording() {
      if (this.mediaRecorder && this.mediaRecorder.stream) {
        this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
      }

      this.mediaStream = null;

      log.info('View:OrderAudioNotes:uploadFormRecording', {
        'message': 'Upload started.',
        'chunkCount': this.audioChunks.length
      });

      const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
      const MIN_FILE_SIZE = 1000; // 1KB

      if (audioBlob.size < MIN_FILE_SIZE) {
        log.warn('View:OrderAudioNotes:uploadRecording', {
          'message': 'Recording stopped, but audio data is too small.',
          'size': audioBlob.size
        });
        this.$swal.fire(
            this.t('alerts.warning'),
            this.t('messages.audioRecordTooShort'),
            'warning'
        );
        return;
      }

      const audioFile = new File([audioBlob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('file', audioFile);

      this.$emit('update:isSubmitting', true);

      try {
        const res = await $http.post('/api/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: this.$store.getters.userToken,
          },
        });

        const uploaded = res.data;
        log.info('View:OrderAudioNotes:uploadRecording:success', { 'file': uploaded });

        this.$emit('uploaded', uploaded);

      } catch (error) {
        log.error('View:OrderAudioNotes:uploadRecording:error', { 'error': error });
        this.$swal.fire(this.t('alerts.error'), this.t('messages.fileNotUploaded'), 'error');
      } finally {
        this.$emit('update:isSubmitting', false);
      }
    },
    handlePlay(audio) {
      this.$emit('read', audio);
    },
    handleDelete(audio) {
      this.$emit('delete', audio);
    }
  }
}
</script>

<template>
  <div class="col-12">
    <div class="card shadow-sm border-0 section-card">
      <div class="card-header bg-light d-flex align-items-center justify-content-between"
           :aria-expanded="isCollapseOpen"
           aria-controls="audioNotesCollapse"
           @click.prevent="toggleCollapse"
           style="cursor: pointer; user-select: none;">
        <span class="fw-semibold d-flex align-items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M9 2m0 3a3 3 0 0 1 3 -3h0a3 3 0 0 1 3 3v5a3 3 0 0 1 -3 3h0a3 3 0 0 1 -3 -3z" />
            <path d="M5 10a7 7 0 0 0 14 0" />
            <path d="M8 21l8 0" />
            <path d="M12 17l0 4" />
          </svg>
          {{ t('labels.audioNotes') }}
          <span v-if="audioRecords.length > 0" class="badge bg-secondary rounded-pill">{{ audioRecords.length }}</span>
        </span>

        <svg :class="{'collapse-arrow-rotated': !isCollapseOpen}" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted" style="transition: transform 0.2s ease;">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M6 9l6 6l6 -6" />
        </svg>
      </div>

      <div :class="{'collapse': true, 'show': isCollapseOpen}" id="audioNotesCollapse">
        <div class="card-body">

          <div class="mb-2" v-if="audioRecords.length > 0">
            <ul class="list-group">
          <li v-for="audio in audioRecords" :key="audio.id" class="list-group-item">
            <div class="d-flex flex-column flex-md-row align-items-md-center gap-2">
              <div class="audio-player-wrap w-100 flex-md-grow-1">
                <vue-plyr @play="handlePlay(audio)">
                  <audio controls preload="metadata">
                    <source :src="audio.url" :type="audio.type || 'audio/mp3'"/>
                  </audio>
                </vue-plyr>
              </div>

              <div class="d-flex align-items-center justify-content-between gap-2 flex-shrink-0">
                <div class="audio-meta">
                  <span class="audio-user" v-if="audio.uploader?.username">
                    {{ audio.uploader.username || audio.uploader.login }}
                  </span>
                  <span class="audio-time" v-if="audio.createdAt">
                    {{ formatFullDate(audio.createdAt) }}
                  </span>
                </div>

                <button
                    type="button"
                    class="btn-close flex-shrink-0"
                    aria-label="Delete"
                    v-if="isOrderCreator"
                    @click="handleDelete(audio)">
                </button>
              </div>
            </div>

            <div v-if="audio.readBy && audio.readBy.length > 0" class="audio-read-by mt-2">
              <strong>Прослухали:</strong>
              <span v-for="user in audio.readBy" :key="user._id" class="badge bg-success text-white ms-1">
                {{ user.username }}
              </span>
            </div>
          </li>
        </ul>
      </div>

          <template v-if="isOrderCreator">
            <div class="mb-1">
              <button
                  type="button"
                  class="btn btn-success w-100"
                  @click="startRecording"
                  v-if="!isRecording">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-microphone" style="vertical-align: text-bottom;">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M9 2m0 3a3 3 0 0 1 3 -3h0a3 3 0 0 1 3 3v5a3 3 0 0 1 -3 3h0a3 3 0 0 1 -3 -3z" />
                  <path d="M5 10a7 7 0 0 0 14 0" />
                  <path d="M8 21l8 0" />
                  <path d="M12 17l0 4" />
                </svg>
                {{ t('buttons.recordAudioNote') }}
              </button>

              <button
                  type="button"
                  class="btn btn-danger w-100"
                  @click="stopRecording"
                  v-if="isRecording">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-player-stop" style="vertical-align: text-bottom;">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M5 5m0 2a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z" />
                </svg>
                {{ t('buttons.stopRecording') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.section-card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;

  .card-header {
    border-bottom: 1px solid #e9ecef;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    font-size: 0.95rem;
  }
}

.collapse-arrow-rotated {
  transform: rotate(-90deg);
}
.audio-meta {
  display: flex;
  flex-direction: column;
  font-size: 0.85rem;
  min-width: 0;

  .audio-user {
    font-weight: bold;
    word-break: break-word;
  }
  .audio-time {
    color: #6c757d;
    font-size: 0.75rem;
    white-space: nowrap;
  }
}

@media (min-width: 768px) {
  .audio-player-wrap {
    min-width: 55%;
  }
}
</style>